/*
 * STL GWP cart sync — re-render the cart when Rebuy changes it.
 *
 * Why this exists: Rebuy's GWP widget (308703) adds/removes the free gift on its own,
 * ~1s AFTER the theme's cart request has already rendered the drawer / cart page.
 * Nothing in the theme hears about that change, so the cart kept showing the old lines
 * (gift missing or lingering, stale progress bar, no empty state) until a reload.
 * Verified with Playwright on the preview theme, 2026-09-14.
 *
 * Rebuy fires `rebuy:cart.change` on document after every cart change (its own and the
 * theme's). Each rendered cart carries its line keys in [data-stl-cart-keys]; when
 * /cart.js no longer matches, re-render the same sections the theme's own cart code
 * renders (CartItems#getSectionsToRender). Deliberately does NOT publish cartUpdate:
 * CartItems#onCartUpdate only swaps part of the drawer and never sets the empty state.
 */
(function () {
  if (window.__stlGwpInit) return;
  window.__stlGwpInit = true;

  var tId = null;
  var running = false;
  var pending = false;
  // Set by theme.liquid when this shopper can't get the gift (outside the US, or a dealer).
  var removeOnly = !!document.currentScript && document.currentScript.hasAttribute('data-stl-gwp-remove');

  function containers() {
    return Array.prototype.slice.call(document.querySelectorAll('cart-items, cart-drawer-items')).filter(function (el) {
      return typeof el.getSectionsToRender === 'function';
    });
  }

  function isStale(cart) {
    var keys = cart.items.map(function (item) { return item.key + ','; }).join('');
    var markers = document.querySelectorAll('[data-stl-cart-keys]');
    for (var i = 0; i < markers.length; i++) {
      if (markers[i].getAttribute('data-stl-cart-keys') !== keys) return true;
    }
    return false;
  }

  function render(cart, sections) {
    var empty = cart.item_count === 0;
    ['cart-drawer', '.cart-order', '#main-cart-footer'].forEach(function (selector) {
      var node = document.querySelector(selector);
      if (node) node.classList.toggle('is-empty', empty);
    });
    containers().forEach(function (el) {
      el.classList.toggle('is-empty', empty);
      el.getSectionsToRender().forEach(function (section) {
        var container = document.getElementById(section.id);
        var html = sections[section.section];
        if (!container || !html) return;
        var source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        // e.g. #main-cart-shipping is not rendered at all once the cart is empty.
        if (!source) return;
        (container.querySelector(section.selector) || container).innerHTML = source.innerHTML;
      });
    });
  }

  /*
   * Move the gift to the LAST cart line, so it is also the last line on the order (OSCO packs
   * in order-line order; a gift on top got orders packed into bigger/multiple boxes).
   *
   * Shopify puts every newly added line at the TOP of the cart and nothing can move a line, so
   * Rebuy's gift (always added last) lands first. Fix: re-add every other line, bottom-up, then
   * remove the originals. Verified against the live cart API, 2026-10-02:
   * - Copies are added BEFORE the originals are removed, so the subtotal never dips below the
   *   gift threshold (a dip would let Rebuy pull the gift).
   * - Copies carry `_stl_line_order: <stamp>` so they can't merge into the originals they replace.
   * - Each parent is re-added in its own request together with its nested children (parent_id is
   *   a variant id resolved within the request), so the native parent/child lines the light bar
   *   builder and Rebuy accessories create survive, even with two parents of the same variant.
   *   Removing an original parent removes its original children with it.
   * - On any failure the copies are removed again, leaving the cart exactly as it was.
   */
  function isGift(item) {
    return !!item.properties && item.properties._widget_id === '308703' && item.final_line_price === 0;
  }

  function needsReorder(cart) {
    var items = cart.items || [];
    return items.length > 1 && items.some(isGift) && !isGift(items[items.length - 1]);
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      if (!response.ok) throw new Error(url + ' ' + response.status);
      return response.json();
    });
  }

  function copyOf(item, stamp, parentVariantId) {
    var properties = {};
    Object.keys(item.properties || {}).forEach(function (name) { properties[name] = item.properties[name]; });
    properties._stl_line_order = stamp;
    var copy = { id: item.variant_id, quantity: item.quantity, properties: properties };
    if (parentVariantId) copy.parent_id = parentVariantId;
    if (item.selling_plan_allocation) copy.selling_plan = item.selling_plan_allocation.selling_plan.id;
    return copy;
  }

  function reorder(cart) {
    var stamp = String(Date.now());
    var parentKey = function (item) { return item.parent_relationship && item.parent_relationship.parent_key; };
    var tops = cart.items.filter(function (item) { return !parentKey(item) && !isGift(item); });

    // Bottom-up: each request lands on top, so adding the last group first keeps the original order.
    var chain = Promise.resolve();
    tops.slice().reverse().forEach(function (top) {
      var batch = [copyOf(top, stamp)];
      cart.items.forEach(function (item) {
        if (parentKey(item) === top.key) batch.push(copyOf(item, stamp, top.variant_id));
      });
      chain = chain.then(function () { return post('/cart/add.js', { items: batch }); });
    });

    var originals = {};
    tops.forEach(function (top) { originals[top.key] = 0; });

    return chain
      .then(function () { return post('/cart/update.js', { updates: originals }); })
      .catch(function (e) {
        console.warn('[stl-gwp] gift reorder failed, rolling back', e);
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) { return response.json(); })
          .then(function (current) {
            var copies = {};
            current.items.forEach(function (item) {
              if (!parentKey(item) && item.properties && item.properties._stl_line_order === stamp) copies[item.key] = 0;
            });
            if (Object.keys(copies).length) return post('/cart/update.js', { updates: copies });
          });
      })
      .then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) { return response.json(); });
      });
  }

  /*
   * Remove-only mode: take the gift out of the cart. Rebuy's discount applies in every market, so a
   * gift added in a US session otherwise stays in the cart at $0 after switching to e.g. Canada.
   */
  function removeGifts(cart) {
    var updates = {};
    cart.items.forEach(function (item) {
      if (item.properties && item.properties._widget_id === '308703') updates[item.key] = 0;
    });
    return Object.keys(updates).length ? post('/cart/update.js', { updates: updates }) : cart;
  }

  function sync() {
    if (running) {
      pending = true;
      return;
    }
    running = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (removeOnly) return removeGifts(cart);
        return needsReorder(cart) ? reorder(cart) : cart;
      })
      .then(function (cart) {
        if (!isStale(cart)) return;
        var names = [];
        containers().forEach(function (el) {
          el.getSectionsToRender().forEach(function (section) {
            if (names.indexOf(section.section) === -1) names.push(section.section);
          });
        });
        if (!names.length) return;
        return fetch(window.location.pathname + '?sections=' + names.join(','))
          .then(function (response) { return response.json(); })
          .then(function (sections) { render(cart, sections); });
      })
      .catch(function (e) {
        console.warn('[stl-gwp] cart sync failed', e);
      })
      .then(function () {
        running = false;
        if (pending) {
          pending = false;
          sync();
        }
      });
  }

  document.addEventListener('rebuy:cart.change', function () {
    clearTimeout(tId);
    tId = setTimeout(sync, 300);
  });

  // Once per page load too, so a gift added just before navigating away still ends up last.
  sync();
})();
